/**
 * EntitlementsMiddleware (02/WS4, Q5/G2) — the BACKEND enforcement half of
 * per-member feature gating: an RPC group that must be entitlement-gated adds
 * `.middleware(EntitlementsMiddleware)` and each handler `yield*`s
 * `requireFeature("<key>")` first. The UI's `hasFeature` is a convenience;
 * THIS is the enforcement (a member cannot call a Max-only RPC by hitting the
 * endpoint directly).
 *
 * ORTHOGONALITY (Q5): this middleware asserts *entitlement* only. Feature-flag
 * gating stays in the flags service; a handler needing both composes
 * `requireFeature(...)` AND the existing flag check. Neither bypasses the
 * other.
 *
 * Tag-only module (pg-free — importable by contracts). The live resolution is
 * in entitlements-middleware-live.ts.
 */
import { RpcMiddleware } from "@effect/rpc"
import { Context, Effect, Schema as S } from "effect"
import { AuthorisationError } from "@/application/shared/schemas/authorisation-schemas"
import type { PlanKey } from "@/contexts/billing/core/domain/plan-key"

/** The caller's resolved entitlements, provided to every handler in the group. */
export class CallerEntitlementsContext extends Context.Tag("CallerEntitlementsContext")<
  CallerEntitlementsContext,
  {
    readonly tier: PlanKey
    readonly features: ReadonlyMap<string, boolean>
  }
>() {}

export class EntitlementsMiddleware extends RpcMiddleware.Tag<EntitlementsMiddleware>()(
  "EntitlementsMiddleware",
  {
    provides: CallerEntitlementsContext,
    failure: AuthorisationError
  }
) {}

/** Feature-catalogue key for the Cova Forms product (minTier: max). */
export const COVA_FORMS_FEATURE_KEY = "forms"

/**
 * Group-level gate for the forms engine. Unlike `EntitlementsMiddleware` +
 * per-handler `requireFeature`, this middleware asserts the forms engine for
 * EVERY rpc in the group, for the requested product (`engineEnabledIn` +
 * `EngineRungGuard`, forms 004 §5.2): core at Max, underwriter at Base. Fails
 * `AuthorisationError` only (the same failure UserMiddleware already
 * contributes to these groups' wire unions), with code `FORMS_NOT_IN_PLAN` or
 * `FORMS_ENTITLEMENT_UNRESOLVED` and never a cause, and still provides
 * `CallerEntitlementsContext` to handlers. Public form-fill APIs (/api/v1/forms, /api/v1/embed) are
 * recipient-facing and MUST NOT carry this gate.
 */
export class FormsEntitlementGateMiddleware extends RpcMiddleware.Tag<FormsEntitlementGateMiddleware>()(
  "FormsEntitlementGateMiddleware",
  {
    provides: CallerEntitlementsContext,
    failure: AuthorisationError
  }
) {}

/** The caller's assigned tier lacks the feature the handler requires. */
export class EntitlementRequiredError extends S.TaggedError<EntitlementRequiredError>()(
  "EntitlementRequiredError",
  { feature: S.String }
) {}

/**
 * Handler-side assertion: declare the feature a handler requires. Fails with
 * `EntitlementRequiredError` when the caller's resolved verdict is not an
 * explicit grant. Plain Effect — usable in any application layer (a non-RPC
 * caller uses `EntitlementsService.resolveMemberEntitlements` + a map check).
 */
export const requireFeature = (
  feature: string
): Effect.Effect<void, EntitlementRequiredError, CallerEntitlementsContext> =>
  Effect.gen(function* () {
    const ent = yield* CallerEntitlementsContext
    if (ent.features.get(feature) !== true) {
      return yield* new EntitlementRequiredError({ feature })
    }
  })
